import { useState } from 'react'

import { Box, Tab, Tabs, Theme, Typography } from '@mui/material'

import { VerticalTabPanel } from 'components/common'

import { AllocationStability } from './AllocationStability'
import { BidAnalysis } from './BidAnalysis'
import { BidsPerJobAnalysis } from './BidsPerJobAnalysis'
import { BidSpreadAnalysis } from './BidSpreadAnalysis'
import { DriverAppFulfilment } from './DriverAppFulfilment'
import { DriverLocationTracking } from './DriverLocationTracking'
import { ExceptionMetrics } from './ExceptionMetrics'
import { NegotiationOverview } from './NegotiationOverview'
import { NetworkGrowth } from './NetworkGrowth'
import { SupplierActivation } from './SupplierActivation'
import { SupplierActivityRecency } from './SupplierActivityRecency'
import { SupplyDemandDistribution } from './SupplyDemandDistribution'
import './chartSetup'

enum MetricTabId {
  DriverLocationTracking,
  SupplyDemandDistribution,
  ExceptionMetrics,
  NetworkGrowth,
  BidAnalysis,
  BidsPerJobAnalysis,
  BidSpreadAnalysis,
  DriverAppFulfilment,
  NegotiationOverview,
  SupplierActivation,
  SupplierActivityRecency,
  AllocationStability,
}

const tabs = [
  { id: MetricTabId.DriverLocationTracking, label: 'Driver Location Tracking' },
  { id: MetricTabId.SupplyDemandDistribution, label: 'Supply-Demand Distribution' },
  { id: MetricTabId.ExceptionMetrics, label: 'Exception Metrics' },
  { id: MetricTabId.NetworkGrowth, label: 'Network Growth & Engagement' },
  { id: MetricTabId.BidAnalysis, label: 'Bidding & Response Times' },
  { id: MetricTabId.BidsPerJobAnalysis, label: 'Bids Per Job Analysis' },
  { id: MetricTabId.BidSpreadAnalysis, label: 'Bid Spread Analysis' },
  { id: MetricTabId.DriverAppFulfilment, label: 'Driver App vs CX Fulfilment' },
  { id: MetricTabId.NegotiationOverview, label: 'Negotiation Overview' },
  { id: MetricTabId.SupplierActivation, label: 'Supplier Creation' },
  { id: MetricTabId.SupplierActivityRecency, label: 'Supplier Activity Recency' },
  { id: MetricTabId.AllocationStability, label: 'Allocation Stability' },
]

const SIDEBAR_WIDTH = 30
const panelWidth = (theme: Theme) => `calc(100% - ${theme.spacing(SIDEBAR_WIDTH)})`

function commonProps(index: MetricTabId) {
  return { id: `metrics-tab-${index}`, 'aria-controls': `metrics-tabpanel-${index}` }
}

const Metrics = () => {
  const [tabValue, setTabValue] = useState<MetricTabId>(MetricTabId.DriverLocationTracking)

  const handleChange = (_: unknown, newValue: MetricTabId) => {
    setTabValue(newValue)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        backgroundColor: (theme) => theme.palette.background.paper,
      }}>
      <Box
        sx={{
          width: (theme) => theme.spacing(SIDEBAR_WIDTH),
          flexShrink: 0,
          boxShadow: (theme) => `inset -2px 0 0 0 ${theme.palette.divider}`,
        }}>
        <Typography
          variant='subtitle1'
          fontWeight={700}
          align='center'
          sx={{ color: 'primary.main', py: 2 }}>
          METRICS
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleChange}
          orientation='vertical'
          aria-label='metrics tabs'
          indicatorColor='primary'
          variant='scrollable'
          scrollButtons='auto'
          sx={{ '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left' } }}>
          {tabs.map((tab) => (
            <Tab key={tab.id} label={tab.label} value={tab.id} {...commonProps(tab.id)} />
          ))}
        </Tabs>
      </Box>

      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.DriverLocationTracking}
        px={3}
        width={panelWidth}>
        <DriverLocationTracking />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.SupplyDemandDistribution}
        px={3}
        width={panelWidth}>
        <SupplyDemandDistribution />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.ExceptionMetrics}
        px={3}
        width={panelWidth}>
        <ExceptionMetrics />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.NetworkGrowth}
        px={3}
        width={panelWidth}>
        <NetworkGrowth />
      </VerticalTabPanel>
      <VerticalTabPanel value={tabValue} index={MetricTabId.BidAnalysis} px={3} width={panelWidth}>
        <BidAnalysis />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.BidsPerJobAnalysis}
        px={3}
        width={panelWidth}>
        <BidsPerJobAnalysis />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.BidSpreadAnalysis}
        px={3}
        width={panelWidth}>
        <BidSpreadAnalysis />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.DriverAppFulfilment}
        px={3}
        width={panelWidth}>
        <DriverAppFulfilment />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.NegotiationOverview}
        px={3}
        width={panelWidth}>
        <NegotiationOverview />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.SupplierActivation}
        px={3}
        width={panelWidth}>
        <SupplierActivation />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.SupplierActivityRecency}
        px={3}
        width={panelWidth}>
        <SupplierActivityRecency />
      </VerticalTabPanel>
      <VerticalTabPanel
        value={tabValue}
        index={MetricTabId.AllocationStability}
        px={3}
        width={panelWidth}>
        <AllocationStability />
      </VerticalTabPanel>
    </Box>
  )
}

export default Metrics
